import { FC, useContext } from "react";
import { onlineManager, useQuery } from "react-query";
import { isUndefined, round } from "lodash";
import {
  CalcByProductsRequest,
  CalcByProductsResponse,
  loan,
  ProductsResponse,
} from "../@types/responses";
import {
  AmountType,
  MAP,
  MapLegendItem,
  SIGN,
  Track,
  TrackCode,
  Values,
} from "../@types/common";
import { BREAKPOINTS } from "../constants/common";
import { APIContext } from "../context/context";
import { translation as t } from "../utils/translation";
import { formatNumberWithoutDot } from "../utils/ui";
import { getPayloadProducts } from "../utils/logic";
import Tooltip from "./tooltip";
import Graph from "./graph";
import MapLegend from "./mapLegend";
import ResultsHeader from "./resultsHeader";
import InfoItem from "./infoItem";
import ErrorModal from "./errorModal";
import { useWindowSize } from "../hooks/useWindowWidth";
import ResultsLinks, {MortgageRequestButton} from "./resultsLinks";
import Loader from "./loader";
interface CalculateByProductResultsProps {
  tracks: loan[];
  paramsData: ProductsResponse;
  setResultError: (value: boolean) => void;
  setOpen: (value: boolean) => void;
  open: boolean;
}
const CalculateByProductResults: FC<CalculateByProductResultsProps> = (
  props: CalculateByProductResultsProps
) => {
  const windowWidth = useWindowSize();
  const { LARGE } = BREAKPOINTS;
  const API_AND_URLS = useContext(APIContext);
  const url =
    API_AND_URLS !== undefined
      ? API_AND_URLS.API.POST.CALCULATE_BY_PRODUCTS
      : "";
  const params: CalcByProductsRequest = {
    loans: props.tracks,
    returnPaymentDetails: false,
    calculatePrimeDaily: false,
  };
  {
    const { isLoading, error, data } = useQuery<
      CalcByProductsResponse,
      Error,
      CalcByProductsResponse,
      [string, CalcByProductsRequest]
    >(
      ["CalculateByProduct", params],
      () =>
        // for Development
        // fetch(url).then((response) => {
        // for Production
        fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(params),
        }).then((response) => {
          if (response.status !== 200) {
            throw new Error();
          }
          // END for Production
          return response.json();
        }),
      {
        retry: false,
        enabled: onlineManager.isOnline(),
      }
    );
    if (isLoading) {
      return <Loader />;
    }
    if (error || isUndefined(data)) {
      props.setResultError(true);
      return <ErrorModal open={props.open} setOpen={props.setOpen} />;
    }
    const productParts: MapLegendItem[] = [];
    const mapLegendItems: MapLegendItem[] = [];
    const GraphItems: MapLegendItem[] = [];
    data.repaymentSummary.loansRepayment.map((loan) => {
      const productPart: MapLegendItem = {
        color: MAP[loan.product.productCode as TrackCode],
        percent: loan.product.productPart,
        text: loan.product.trackName,
      };
      productParts.push(productPart);
    });
    const values: Values = { tracks: props.tracks as unknown as Track[] };
    return (
      <>
        <ResultsHeader
          loanAmount={data.repaymentSummary.totalRequestedAmount}
          period={data.repaymentSummary.period}
        />
        <MortgageRequestButton params={getPayloadProducts(values, props.paramsData)} position={'Top'} />
        <div className="flex flex-col justify-center">
          <div className="flex flex-col justify-center xs:py-[10px] xxl:py-[50px] xs:px-[20px] xxl:px-[140px] bg-lightBlue rounded-m ">
            <div
              className={`font-semibold text-darkBlue xs:text-[18px] xxl:text-[22px] xs:py-[10px] xxl:py-[25px] ${
                windowWidth < LARGE ? "self-center" : "self-baseline"
              }`}
            >
              {t.CUSTOM_MIX}
            </div>
            <div
              className={`flex justify-around ${
                windowWidth < LARGE ? "flex-col" : "flex-row"
              }`}
            >
              <InfoItem
                title={t.TOTAL_AMOUNT}
                description={t.TOOLTIP.AMOUNT_AT_THE_END_OF_THE_PERIOD}
                amount={
                  {
                    value: formatNumberWithoutDot(
                      data.repaymentSummary.totalAmountPaid.toString()
                    ),
                    sign: SIGN.ILS,
                  } as unknown as AmountType
                }
              />
              <div className={`${windowWidth < LARGE ? "mr-0" : "mr-5"}`}>
                <InfoItem
                  title={t.FIRST_PAYMENT}
                  description={t.TOOLTIP.FIRST_MONTHLY_REPAYMENT}
                  amount={
                    {
                      value: formatNumberWithoutDot(
                        data.repaymentSummary.totalFirstPayment.toString()
                      ),
                      sign: SIGN.ILS,
                    } as unknown as AmountType
                  }
                />
              </div>
              <div className={`${windowWidth < LARGE ? "mx-0" : "mx-5"}`}>
                <InfoItem
                  title={t.HIGHEST_PAYMENT}
                  description={t.TOOLTIP.MAXIMUM_MONTHLY_REPAYMENT}
                  amount={
                    {
                      value: formatNumberWithoutDot(
                        data.repaymentSummary.totalHighestPayment.toString()
                      ),
                      sign: SIGN.ILS,
                    } as unknown as AmountType
                  }
                />
              </div>
              <InfoItem
                title={t.TOTAL_INTEREST}
                description={t.TOOLTIP.PREDICTED_TOTAL_INTEREST}
                amount={
                  {
                    value: round(data.repaymentSummary.totalIrr, 2).toString(),
                    sign: SIGN.PERCENT,
                  } as unknown as AmountType
                }
              />
            </div>
            <div className="flex justify-between items-center pb-[10px]">
              <label className="text-darkBlue xs:font-semibold xxl:font-normal xs:text-[18px] xxl:text-[22px]">
                {t.MORTGAGE_PERCENTS}
              </label>
              <Tooltip message={t.TOOLTIP.MORTGAGE_PERCENTS} />
            </div>
            <div className="productPartGraph ">
              {productParts.map((item, index) => {
                GraphItems.push(item);
                return <span key={index}></span>;
              })}
              <Graph items={GraphItems} />
            </div>
            <div className="self-center productPartMapLegend xs:w-[200px] xxl:w-[250px]">
              {productParts.map((item, index) => {
                const mapLegendItem: MapLegendItem = {
                  color: item.color,
                  percent: item.percent,
                  text: item.text,
                };
                mapLegendItems.push(mapLegendItem);
                return <span key={index}></span>;
              })}
              <MapLegend items={mapLegendItems} />
            </div>
          </div>
          <ResultsLinks params={getPayloadProducts(values, props.paramsData)} position={'bottom'}/>
        </div>
      </>
    );
  }
};
export default CalculateByProductResults;